import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Video, Calendar, Clock, Plus, Loader2, X, MoreVertical, Users, Activity } from 'lucide-react';
import { format, isToday } from 'date-fns';
import { FloatingVideoCall } from '@/components/video/FloatingVideoCall';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { watchUpcomingAppointments, cancelAppointment } from '@/lib/firebase/queries/appointments';
import { AvailabilityService } from '@/lib/services/availabilityService';
import { getUserById } from '@/lib/firebase/queries/users';
import { toast } from 'sonner';
import { BookSessionDialog } from './BookSessionDialog';
import type { Appointment } from '@/types';
import { collection, query, where, getDocs, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface Session {
  id: string;
  coachId: string;
  coachName: string;
  customerName: string;
  date: Date;
  status: string;
}

interface Coach {
  id: string;
  firstNameAr: string;
  lastNameAr: string;
  firstNameEn: string;
  lastNameEn: string;
}

interface OpenClinic {
  coachId: string;
  coachName: string;
  coachNameAr?: string;
  coachNameEn?: string;
  customersCount: number;
}

interface CoachingSessionsProps {
  organizationId?: string;
}

export function CoachingSessions({ organizationId }: CoachingSessionsProps) {
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [coachesLoading, setCoachesLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [callStatuses, setCallStatuses] = useState<{[key: string]: boolean}>({});
  const { t, language, dir } = useLanguage();
  const { user, currentUser, userData } = useAuth();
  
  // Open clinics state
  const [openClinics, setOpenClinics] = useState<OpenClinic[]>([]);
  const [loadingClinics, setLoadingClinics] = useState(true);

  useEffect(() => {
    const fetchCoaches = async () => {
      setCoachesLoading(true);
      try {
        const coachesQuery = query(collection(db, 'users'), where('role', '==', 'coach'));
        const coachesSnapshot = await getDocs(coachesQuery);
        const coachList: Coach[] = coachesSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            firstNameAr: data.firstNameAr || '',
            lastNameAr: data.lastNameAr || '',
            firstNameEn: data.firstNameEn || '',
            lastNameEn: data.lastNameEn || '',
          };
        });
        setCoaches(coachList);
      } catch (error) {
        // handle error
      } finally {
        setCoachesLoading(false);
      }
    };
    fetchCoaches();
  }, []);

  // Fetch open clinics
  useEffect(() => {
    setLoadingClinics(true);
    
    const unsubscribe = AvailabilityService.listenToOpenClinics((coaches) => {
      const clinics = coaches.map(coach => ({
        coachId: coach.coachId,
        coachName: language === 'ar' ? (coach.coachNameAr || coach.coachName) : (coach.coachNameEn || coach.coachName),
        coachNameAr: coach.coachNameAr,
        coachNameEn: coach.coachNameEn,
        customersCount: coach.customersCount
      }));
      setOpenClinics(clinics);
      setLoadingClinics(false);
    });

    return () => unsubscribe();
  }, [language]);

  const handleJoinClinic = (clinic: OpenClinic) => {
    // Open the public clinic waiting room page in a new tab
    window.open(`/clinic/${clinic.coachId}`, '_blank');
  };

  useEffect(() => {
    if (!user || !user.uid) return;

    const unsubscribe = watchUpcomingAppointments(
      user.uid,
      'customer',
      async (appointments: Appointment[]) => {
        try {
          const sessionsWithCoachNames = await Promise.all(
            appointments.map(async (appointment) => {
              const coach = await getUserById(appointment.coachId);
              if (!coach) {
                throw new Error(`Coach not found: ${appointment.coachId}`);
              }
              return {
                id: appointment.id,
                coachId: appointment.coachId,
                coachName: language === 'ar' 
                  ? `${coach.firstNameAr} ${coach.lastNameAr}`
                  : `${coach.firstNameEn} ${coach.lastNameEn}`,
                customerName: language === 'ar'
                  ? `${user.firstNameAr || user.firstName || ''} ${user.lastNameAr || user.lastName || ''}`.trim() || 'مستفيد'
                  : `${user.firstNameEn || user.firstName || ''} ${user.lastNameEn || user.lastName || ''}`.trim() || 'Customer',
                date: appointment.date instanceof Date ? appointment.date : appointment.date.toDate(),
                status: appointment.status
              };
            })
          );
          setSessions(sessionsWithCoachNames);
        } catch (error) {
          console.error('Error fetching coach details:', error);
          toast.error(t('coaching.errorFetchingCoachDetails'));
        } finally {
          setLoading(false);
        }
      },
      (error: Error) => {
        console.error('Error in appointments listener:', error, error.stack);
        toast.error(t('coaching.errorFetchingAppointments'));
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user, language, t]);

  // Set up real-time listeners for call statuses when sessions change
  useEffect(() => {
    if (sessions.length === 0) return;

    const unsubscribeFunctions: (() => void)[] = [];

    // Set up real-time listener for each session
    sessions.forEach(session => {
      const coachStatusRef = doc(db, 'calls', session.id, 'status', 'coach');
      
      const unsubscribe = onSnapshot(coachStatusRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
          const coachData = docSnapshot.data();
          const isCoachOnline = coachData.joined || false;
          
          setCallStatuses(prev => ({
            ...prev,
            [session.id]: isCoachOnline
          }));
          
          console.log(`👥 [CoachingSessions] Coach presence for session ${session.id}:`, isCoachOnline);
        } else {
          setCallStatuses(prev => ({
            ...prev,
            [session.id]: false
          }));
        }
      }, (error) => {
        console.error('Error listening to coach status:', error);
      });

      unsubscribeFunctions.push(unsubscribe);
    });

    // Cleanup all listeners when sessions change or component unmounts
    return () => {
      unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
    };
  }, [sessions]);

  const checkCallStatus = async (sessionId: string) => {
    try {
      // Check if the other party (coach) is currently online in the call
      const coachStatusRef = doc(db, 'calls', sessionId, 'status', 'coach');
      const coachStatusSnapshot = await getDoc(coachStatusRef);
      
      if (coachStatusSnapshot.exists()) {
        const coachData = coachStatusSnapshot.data();
        return coachData.joined || false;
      }
      
      return false;
    } catch (error) {
      console.error('Error checking call status:', error);
      return false;
    }
  };

  // Soft cancel (namat#36 P1-7): the row stays as status 'cancelled' with
  // the canceller's uid, like mobile, instead of being deleted.
  const handleCancelAppointment = async () => {
    if (!cancellingId) return;

    try {
      await cancelAppointment(cancellingId, user?.uid ?? null);
      
      toast.success(t('coaching.cancelSuccess'));
      setShowCancelDialog(false);
      setCancellingId(null);
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      toast.error(t('coaching.cancelError'));
    }
  };

  return (
    <>
      {/* Clinics & Appointments Section - Combined Widget */}
      <Card className="overflow-hidden">
        <CardHeader className="border-b bg-muted/50">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Video className="h-5 w-5 text-primary" />
              {t('coaching.yourAppointments')}
            </CardTitle>
            <Button
              onClick={() => setShowBookingDialog(true)}
              size="sm"
              variant="outline"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('coaching.bookSession')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {(loading || loadingClinics) ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Open Clinics - shown first as items */}
              {openClinics.map((clinic) => (
                <motion.div
                  key={`clinic-${clinic.coachId}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="border border-green-200 rounded-lg p-4 bg-green-50/50 hover:bg-green-50 transition-colors"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <div className="font-medium flex items-center gap-2">
                          <Activity className="h-4 w-4 text-green-600" />
                          {t('coaching.withCoach')} {clinic.coachName}
                        </div>
                        <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs">
                          {t('coaching.open')}
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        <span>{clinic.customersCount} {t('coaching.waiting')}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-end gap-2 w-full sm:w-auto">
                      <Button
                        onClick={() => handleJoinClinic(clinic)}
                        className="bg-[#7dda55] hover:bg-[#6dc447] text-white"
                        size="sm"
                      >
                        <Video className="h-4 w-4 mr-2" />
                        {t('coaching.joinNow')}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}

              {/* Scheduled Appointments */}
              {sessions.map((session, index) => {
                // Same rule as CoachingPage: a call is only for a scheduled
                // session on its own day.
                const canCall = isToday(session.date) && session.status === 'scheduled';
                return (
                <motion.div
                  key={session.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: (openClinics.length + index) * 0.1 }}
                  className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <div className="font-medium">
                          {language === 'ar' ? (
                            <>
                              {t('coaching.withCoach')} {session.coachName}
                            </>
                          ) : (
                            <>
                              {t('coaching.withCoach')} {session.coachName}
                            </>
                          )}
                        </div>
                        {session.status === 'scheduled' && (
                          <div className="sm:hidden">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align={dir === 'rtl' ? 'start' : 'end'}>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setCancellingId(session.id);
                                    setShowCancelDialog(true);
                                  }}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <X className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                                  {t('coaching.cancel')}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <Badge variant="outline" className="mr-2">
                          {t(`coaching.clinicStatus.${session.status}`)}
                        </Badge>
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {format(session.date, 'PPP')}
                        </span>
                        <span className="inline-flex items-center gap-1 ml-2">
                          <Clock className="h-3 w-3" />
                          {format(session.date, 'p')}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-end gap-2 w-full sm:w-auto">
                      {session.status === 'scheduled' && (
                        <div className="hidden sm:block">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align={dir === 'rtl' ? 'start' : 'end'}>
                              <DropdownMenuItem
                                onClick={() => {
                                  setCancellingId(session.id);
                                  setShowCancelDialog(true);
                                }}
                                className="text-destructive focus:text-destructive"
                              >
                                  <X className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                                  {t('coaching.cancel')}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}
                      <Button
                        onClick={() => {
                          if (!canCall) {
                            toast.error(t('coaching.callOnlyToday'));
                            return;
                          }
                          setSelectedSession(session);
                          setShowVideoCall(true);
                        }}
                        className="bg-[#7dda55] hover:bg-[#6dc447] text-white"
                        size="sm"
                        disabled={!canCall}
                      >
                        <Video className="h-4 w-4 mr-2" />
                        {callStatuses[session.id] ? t('coaching.joinCall') : t('coaching.startCall')}
                      </Button>
                    </div>
                  </div>
                </motion.div>
                );
              })}

              {/* Empty state when no clinics or sessions */}
              {openClinics.length === 0 && sessions.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  {t('coaching.noUpcomingSessions')}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {showVideoCall && selectedSession && (
        <FloatingVideoCall
          roomId={selectedSession.id}
          userRole="customer"
          userName={selectedSession.customerName}
          otherParticipantName={
            language === 'ar' 
              ? `المدرب ${selectedSession.coachName}`
              : selectedSession.coachName
          }
          onCallEnded={() => setShowVideoCall(false)}
          open={showVideoCall}
          appointmentDate={selectedSession.date}
        />
      )}

      <BookSessionDialog
        open={showBookingDialog}
        onOpenChange={setShowBookingDialog}
        coaches={coaches}
      />

      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent dir={dir}>
          <AlertDialogHeader className={dir === 'rtl' ? 'text-right' : 'text-left'}>
            <AlertDialogTitle>
              {t('coaching.cancelConfirmTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('coaching.cancelConfirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className={`flex ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
            <AlertDialogCancel className={dir === 'rtl' ? '!ml-3' : '!mr-3'}>
              {t('coaching.cancelNo')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelAppointment}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('coaching.cancelYes')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}